import React, { useState, useMemo } from 'react';
import {
  Box, Button, Group, Loader, Alert, Text, Title, Table,
  ScrollArea, Paper, TextInput, Select, Badge, Stack
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import SalespersonSelect from '../salesperson/SalespersonSelect';
import { GET_SALESPEOPLE_QUERY } from '../../../queries';

function buildUrl(ccode, from, to, isValid, page) {
  const params = new URLSearchParams();
  if (ccode) params.set('ccode', ccode);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (isValid != null) params.set('isValid', isValid);
  params.set('page', page);
  return `${config.apiBaseUrl}/sales/invoice-overrides/all?${params}`;
}

function fmt(dt) {
  return dt ? new Date(dt).toLocaleString() : '—';
}

export default function AllOverrides() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const today = new Date();
  const ninetyDaysAgo = new Date(today);
  ninetyDaysAgo.setDate(today.getDate() - 90);

  const paramCcode   = searchParams.get('ccode') ?? '';
  const paramFrom    = searchParams.get('from');
  const paramTo      = searchParams.get('to');
  const paramIsValid = searchParams.get('isValid');

  const [ccode, setCcode] = useState(paramCcode);
  const [from, setFrom]   = useState(paramFrom ? new Date(paramFrom) : ninetyDaysAgo);
  const [to, setTo]       = useState(paramTo   ? new Date(paramTo)   : today);
  const [isValidFilter, setIsValidFilter] = useState(
    paramIsValid === 'true' ? true : paramIsValid === 'false' ? false : null
  );
  const [page, setPage]   = useState(0);
  const [search, setSearch] = useState('');

  const fromStr = from?.toISOString().split('T')[0];
  const toStr = to?.toISOString().split('T')[0];

  const { data, isLoading, error } = useAuthenticatedData(
    buildUrl(ccode, fromStr, toStr, isValidFilter, page)
  );

  const { data: salespeopleData } = useQuery(GET_SALESPEOPLE_QUERY);
  const ccodeToName = useMemo(() => {
    const map = {};
    (salespeopleData?.salespeople ?? []).forEach(p => { map[p.cCode] = p.fullName; });
    return map;
  }, [salespeopleData]);

  const rows = (Array.isArray(data) ? data : []).filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.docNo?.toLowerCase().includes(s) ||
      r.salespersonCcode?.toLowerCase().includes(s) ||
      r.addedByUser?.toLowerCase().includes(s)
    );
  });

  return (
    <Box p="xl">
      <Group mb="lg">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/sales/manager')}>
          Back
        </Button>
        <Title order={2}>All Adjustments</Title>
      </Group>

      <Paper withBorder p="md" mb="md">
        <Group gap="md" wrap="wrap">
          <SalespersonSelect
            onSalespersonSelect={person => setCcode(person?.cCode ?? '')}
          />
          <DatePickerInput label="From" value={from} onChange={setFrom} style={{ width: 160 }} clearable />
          <DatePickerInput label="To" value={to} onChange={setTo} style={{ width: 160 }} clearable />
          <Select
            label="Status"
            data={[
              { value: '', label: 'All' },
              { value: 'true', label: 'Active only' },
              { value: 'false', label: 'Superseded only' },
            ]}
            value={isValidFilter == null ? '' : String(isValidFilter)}
            onChange={v => setIsValidFilter(v === '' ? null : v === 'true')}
            style={{ width: 150 }}
          />
          <TextInput
            label="Search"
            placeholder="docNo, ccode, user…"
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            style={{ width: 200 }}
          />
        </Group>
      </Paper>

      {isLoading && <Loader />}
      {error && (
        <Alert icon={<IconAlertCircle />} color="red" mb="md">
          Failed to load adjustments.
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <Paper withBorder>
            <ScrollArea>
              <Table highlightOnHover style={{ fontSize: 13 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Doc No</Table.Th>
                    <Table.Th>Line</Table.Th>
                    <Table.Th>Min Sell Price</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Salesperson</Table.Th>
                    <Table.Th>Added By</Table.Th>
                    <Table.Th>Added On</Table.Th>
                    <Table.Th>Valid From</Table.Th>
                    <Table.Th>Valid To</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map(r => (
                    <Table.Tr
                      key={r.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/sales/manager/invoices/${encodeURIComponent(r.docNo)}/${r.line}`)}
                    >
                      <Table.Td>{r.docNo}</Table.Td>
                      <Table.Td>{r.userLine ?? r.line}</Table.Td>
                      <Table.Td>{r.minSellPrice != null ? `$${r.minSellPrice.toLocaleString()}` : '—'}</Table.Td>
                      <Table.Td>
                        <Badge color={r.isValid ? 'green' : 'gray'} size="sm">
                          {r.isValid ? 'Active' : 'Superseded'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{ccodeToName[r.salespersonCcode] ?? r.salespersonCcode}</Table.Td>
                      <Table.Td>{r.addedByUser}</Table.Td>
                      <Table.Td>{fmt(r.dateAdded)}</Table.Td>
                      <Table.Td>{fmt(r.validFrom)}</Table.Td>
                      <Table.Td>{r.validTo ? fmt(r.validTo) : '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                  {rows.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={9}>
                        <Text c="dimmed" ta="center" py="md">No adjustment records found.</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Text size="sm" c="dimmed" style={{ alignSelf: 'center' }}>Page {page + 1}</Text>
            <Button variant="subtle" disabled={rows.length < 200} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Group>
        </>
      )}
    </Box>
  );
}
